import { Transporter } from './transporter.model';

export class Vehicle {
  public id!: string;
  public transporter!: Transporter;
  public category!: VehicleCategory;
  public type!: VehicleType;
  public length?: number;
  public width?: number;
  public height?: number;
  public payloadValue!: number;
  public surface?: number;
  public volume?: number;
  public createdDate!: Date;
  public updatedDate!: Date;

  constructor(data: any = {}) {
    Object.assign(this, data);
    this.category = LIST_VEHICLE_CATEGORY[data.category || VehicleCategoryEnum.RIGID_LORRY];
    this.type = LIST_VEHICLE_TYPE[data.type || VehicleTypeEnum.TAUTLINER];
    this.transporter = new Transporter(data.transporter);
  }

  public getSurface(): number | null {
    if (this.surface) return this.surface;
    if (this?.length && this?.width) return this.length * this.width;
    return null;
  }

  public getVolume(): number | null {
    if (this.volume) return this.volume;
    if (this.length && this.width && this.height) return this.length * this.width * this.height;
    return null;
  }

  serialize(): any {
    return {
      id: this.id,
      transporter: { id: this.transporter.id },
      category: this.category.code,
      type: this.type?.code,
      length: this.length,
      width: this.width,
      height: this.height,
      payloadValue: this.payloadValue,
      surface: this.surface,
      volume: this.volume,
      createdDate: this.createdDate,
      updatedDate: this.updatedDate,
    };
  }
}

export enum VehicleCategoryEnum {
  LIGHT_VEHICLE = 'LIGHT_VEHICLE',
  TRUCK_WITH_TRAILER = 'TRUCK_WITH_TRAILER',
  RIGID_LORRY = 'RIGID_LORRY',
  ARTICULATED_UNIT = 'ARTICULATED_UNIT',
}

export class VehicleCategory {
  public code!: string;
  public labelKey!: string;

  constructor(data: any = {}) {
    Object.assign(this, data);
  }
}

export const LIST_VEHICLE_CATEGORY: { [key: string]: VehicleCategory } = {
  LIGHT_VEHICLE: new VehicleCategory({ code: VehicleCategoryEnum.LIGHT_VEHICLE, labelKey: 'VEHICLE.CATEGORY.LIGHT_VEHICLE' }),
  TRUCK_WITH_TRAILER: new VehicleCategory({ code: VehicleCategoryEnum.TRUCK_WITH_TRAILER, labelKey: 'VEHICLE.CATEGORY.TRUCK_WITH_TRAILER' }),
  RIGID_LORRY: new VehicleCategory({ code: VehicleCategoryEnum.RIGID_LORRY, labelKey: 'VEHICLE.CATEGORY.RIGID_LORRY' }),
  ARTICULATED_UNIT: new VehicleCategory({ code: VehicleCategoryEnum.ARTICULATED_UNIT, labelKey: 'VEHICLE.CATEGORY.ARTICULATED_UNIT' }),
};

export class MeasureUnit {
  public value!: string;
  public label!: string;

  constructor(data: any = {}) {
    Object.assign(this, data);
  }
}

export enum MeasureUnitEnum {
  KG = 'KG',
  TONNE = 'TONNE',
  METER = 'METER',
  LITER = 'LITER',
  CENTIMETER = 'CENTIMETER',
  SQUARE_METER = 'SQUARE_METER',
  CUBIC_METER = 'CUBIC_METER',
  CELSIUS = 'CELSIUS',
  KG_CUBIC_METER = 'KG_CUBIC_METER',
}

export const LIST_MEASURE_UNIT: { [key: string]: MeasureUnit } = {
  KG: new MeasureUnit({ value: MeasureUnitEnum.KG, label: 'kg' }),
  TONNE: new MeasureUnit({ value: MeasureUnitEnum.TONNE, label: 'T' }),
  LITER: new MeasureUnit({ value: MeasureUnitEnum.LITER, label: 'L' }),
  METER: new MeasureUnit({ value: MeasureUnitEnum.METER, label: 'm' }),
  CENTIMETER: new MeasureUnit({ value: MeasureUnitEnum.CENTIMETER, label: 'cm' }),
  SQUARE_METER: new MeasureUnit({ value: MeasureUnitEnum.SQUARE_METER, label: 'm²' }),
  CUBIC_METER: new MeasureUnit({ value: MeasureUnitEnum.CUBIC_METER, label: 'm³' }),
  KG_CUBIC_METER: new MeasureUnit({ value: MeasureUnitEnum.KG_CUBIC_METER, label: 'kg/m³' }),
  CELSIUS: new MeasureUnit({ value: MeasureUnitEnum.CELSIUS, label: '°C' }),
};

export enum VehicleTypeEnum {
  TAUTLINER = 'TAUTLINER',
  SAVOYARDE = 'SAVOYARDE',
  STANDARD_VAN = 'STANDARD_VAN',
  LARGE_VOLUME = 'LARGE_VOLUME',
  DOUBLE_DECK = 'DOUBLE_DECK',
  CITYLINER = 'CITYLINER',
  FLATBED = 'FLATBED',
  CONTAINER_CARRIER = 'CONTAINER_CARRIER',
  SIMPLE_ISOTHERMAL = 'SIMPLE_ISOTHERMAL',
  MONO_TEMPERATURE_REEFER = 'MONO_TEMPERATURE_REEFER',
  BI_TEMPERATURE_REEFER = 'BI_TEMPERATURE_REEFER',
  MULTI_TEMPERATURE_REEFER = 'MULTI_TEMPERATURE_REEFER',
}

export class VehicleType {
  public code!: string;
  public labelKey!: string;

  constructor(data: any = {}) {
    Object.assign(this, data);
  }
}

export const LIST_VEHICLE_TYPE: { [key: string]: VehicleType } = {
  TAUTLINER: new VehicleType({ code: VehicleTypeEnum.TAUTLINER, labelKey: 'VEHICLE.TYPE.TAUTLINER' }),
  SAVOYARDE: new VehicleType({ code: VehicleTypeEnum.SAVOYARDE, labelKey: 'VEHICLE.TYPE.SAVOYARDE' }),
  STANDARD_VAN: new VehicleType({ code: VehicleTypeEnum.STANDARD_VAN, labelKey: 'VEHICLE.TYPE.STANDARD_VAN' }),
  LARGE_VOLUME: new VehicleType({ code: VehicleTypeEnum.LARGE_VOLUME, labelKey: 'VEHICLE.TYPE.LARGE_VOLUME' }),
  DOUBLE_DECK: new VehicleType({ code: VehicleTypeEnum.DOUBLE_DECK, labelKey: 'VEHICLE.TYPE.DOUBLE_DECK' }),
  CITYLINER: new VehicleType({ code: VehicleTypeEnum.CITYLINER, labelKey: 'VEHICLE.TYPE.CITYLINER' }),
  FLATBED: new VehicleType({ code: VehicleTypeEnum.FLATBED, labelKey: 'VEHICLE.TYPE.FLATBED' }),
  CONTAINER_CARRIER: new VehicleType({ code: VehicleTypeEnum.CONTAINER_CARRIER, labelKey: 'VEHICLE.TYPE.CONTAINER_CARRIER' }),
  SIMPLE_ISOTHERMAL: new VehicleType({ code: VehicleTypeEnum.SIMPLE_ISOTHERMAL, labelKey: 'VEHICLE.TYPE.SIMPLE_ISOTHERMAL' }),
  MONO_TEMPERATURE_REEFER: new VehicleType({ code: VehicleTypeEnum.MONO_TEMPERATURE_REEFER, labelKey: 'VEHICLE.TYPE.MONO_TEMPERATURE_REEFER' }),
  BI_TEMPERATURE_REEFER: new VehicleType({ code: VehicleTypeEnum.BI_TEMPERATURE_REEFER, labelKey: 'VEHICLE.TYPE.BI_TEMPERATURE_REEFER' }),
  MULTI_TEMPERATURE_REEFER: new VehicleType({ code: VehicleTypeEnum.MULTI_TEMPERATURE_REEFER, labelKey: 'VEHICLE.TYPE.MULTI_TEMPERATURE_REEFER' }),
};
export const LIST_CLASSIC_VEHICLE_TYPE: VehicleType[] = [
  LIST_VEHICLE_TYPE[VehicleTypeEnum.TAUTLINER],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.SAVOYARDE],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.STANDARD_VAN],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.LARGE_VOLUME],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.DOUBLE_DECK],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.CITYLINER],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.FLATBED],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.CONTAINER_CARRIER],
];

export const LIST_FRIGO_VEHICLE_TYPE: VehicleType[] = [
  LIST_VEHICLE_TYPE[VehicleTypeEnum.SIMPLE_ISOTHERMAL],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.MONO_TEMPERATURE_REEFER],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.BI_TEMPERATURE_REEFER],
  LIST_VEHICLE_TYPE[VehicleTypeEnum.MULTI_TEMPERATURE_REEFER],
];
